// Annotation Portal React UI for Manage Annotators

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTranslation } from "hooks/useTranslation";

import AddAnnotator from "layouts/admin/annotator/modals/AddAnnotator";
import EditUser from "layouts/admin/annotator/modals/EditUser";
import DeleteUser from "layouts/admin/annotator/modals/DeleteUser";
import ReassignSamples from "layouts/admin/annotator/modals/ReassignSamples";
import ResetPassword from "layouts/super_admin/admin/modals/ResetPassword";
import UsersDataTable from "layouts/admin/annotator/data/UsersDataTable";

import { getAllAnnotators, getAllUsersIncludingDeleted } from "services/userService";

import { getAllAnnotatorsDeleted } from "services/sampleService";

import { getUserInfo } from "services/authService";

// Styled component for fixed-width tabs with rectangle borders
const StyledTab = styled(Tab)(({ theme, selected }) => ({
  minWidth: "250px",
  width: "250px",
  height: "48px",
  border: `2px solid ${selected ? "#2196f3" : "#e0e0e0"}`,
  borderRadius: "4px",
  marginRight: theme.spacing(2),
  "&.Mui-selected": {
    backgroundColor: "transparent",
    color: "#2196f3",
  },
  "&:not(.Mui-selected)": {
    color: "#757575",
  },
}));

function ManageAnnotators() {
  const { t } = useTranslation();
  const user = getUserInfo();

  const [activeTab, setActiveTab] = useState("active"); // active | deleted
  const [openAddAnnotator, setOpenAddAnnotator] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [openReassign, setOpenReassign] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [allAnnotatorList, setAllAnnotatorList] = useState([]);
  const [annotatorList, setAnnotatorList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters (ACTIVE TAB ONLY)
  const [filterAnnotator, setFilterAnnotator] = useState("Created By Me");

  /* ------------------------------------------------------------------ */
  /* Fetch Annotators based on Active / Deleted tab                       */
  /* ------------------------------------------------------------------ */
  const fetchAnnotatorsData = async () => {
    try {
      setLoading(true);

      /* ---------------- ACTIVE TAB ---------------- */
      if (activeTab === "active") {
        const res = await getAllAnnotators();
        const allUsers = await getAllUsersIncludingDeleted();

        const modifiedData = res.map((item) => {
          const creator = allUsers.find((u) => Number(u?.id) === Number(item?.created_by));

          return {
            ...item,
            created_by_id: item.created_by,
            created_by: `${creator?.first_name ?? "-"} ${creator?.last_name ?? "-"}`,
          };
        });

        setAllAnnotatorList(modifiedData);

        // Apply current filter on fetch
        if (filterAnnotator === "All") {
          setAnnotatorList(modifiedData);
        } else {
          const filtered = modifiedData.filter((a) => Number(a.created_by_id) === user.id);
          setAnnotatorList(filtered);
        }
      }

      /* ---------------- DELETED TAB ---------------- */
      if (activeTab === "deleted") {
        const deletedData = await getAllAnnotatorsDeleted();

        const allUsers = await getAllUsersIncludingDeleted();

        // As we want to show created_by as name therefore need to have modified deletedData array
        // also need show deleted accounts which are deleted by logged admin
        let modifiedData = [];

        deletedData.forEach((item) => {
          let portal_user = allUsers.find((elem) => elem?.id == item?.created_by);
          item.created_by = `${portal_user?.first_name ?? "-"} ${portal_user?.last_name ?? "-"}`;
          if (item.deleted_by == user.id) modifiedData.push(item);
        });

        setAllAnnotatorList(modifiedData || []);
        setAnnotatorList(modifiedData || []);
      }
    } catch (error) {
      console.error("Failed to fetch annotators:", error);
      setAllAnnotatorList([]);
      setAnnotatorList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnotatorsData();
  }, [activeTab]);

  /* ------------------------------------------------------------------ */
  /* Handlers                                                           */
  /* ------------------------------------------------------------------ */
  const handleEdit = (user) => {
    setSelectedUser(user);
    setOpenEdit(true);
  };

  const handleReset = (user) => {
    setSelectedUser(user);
    setOpenReset(true);
  };

  const handleReassign = (user) => {
    setSelectedUser(user);
    setOpenReassign(true);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setOpenDelete(true);
  };

  const handleFilterSubmit = () => {
    if (activeTab === "active") {
      if (filterAnnotator === "All") {
        setAnnotatorList(allAnnotatorList);
      } else {
        const filtered = allAnnotatorList.filter((a) => Number(a.created_by_id) === user.id);
        setAnnotatorList(filtered);
      }
    }
  };

  useEffect(() => {
    handleFilterSubmit();
  }, [filterAnnotator, allAnnotatorList, activeTab]);

  const handleFilterReset = () => {
    if (activeTab === "active") {
      setFilterAnnotator("Created By Me");
      const filtered = allAnnotatorList.filter((a) => Number(a.created_by_id) === user.id);
      setAnnotatorList(filtered);
    }
  };

  // Get columns and rows from UsersDataTable
  const { columns: allColumns, rows } = UsersDataTable({
    users: annotatorList,
    onEdit: activeTab === "active" ? handleEdit : null,
    onReset: activeTab === "active" ? handleReset : null,
    onReassign: activeTab === "active" ? handleReassign : null,
    onDelete: activeTab === "active" ? handleDelete : null,
    t,
  });

  // Remove Actions column for Deleted tab
  const columns =
    activeTab === "deleted" ? allColumns.filter((col) => col.Header !== "Actions") : allColumns;

  /* ------------------------------------------------------------------ */
  /* UI                                                                 */
  /* ------------------------------------------------------------------ */
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={6} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Header */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">
                {t("admin.annotators.title", "Annotators")} {loading && "(loading...)"}
              </MDTypography>

              {activeTab === "active" && (
                <MDButton variant="gradient" color="info" onClick={() => setOpenAddAnnotator(true)}>
                  <Icon>add</Icon>&nbsp;
                  {t("admin.annotators.buttons.addAnnotator", "Add Annotator")}
                </MDButton>
              )}
            </MDBox>

            {/* Tabs - Fixed width rectangular with colored borders */}
            <MDBox mt={3}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  "& .MuiTabs-indicator": {
                    display: "none", // Hide the default indicator
                  },
                }}
              >
                <StyledTab
                  label={t("admin.annotators.active", "Active")}
                  value="active"
                  selected={activeTab === "active"}
                />
                <StyledTab
                  label={t("admin.annotators.deleted", "Deleted")}
                  value="deleted"
                  selected={activeTab === "deleted"}
                />
              </Tabs>
            </MDBox>

            {/* Filters – ACTIVE ONLY */}
            {activeTab === "active" && (
              <MDBox mt={3} mb={2}>
                <Card sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>{t("admin.annotators.filters.filter_annotators")}</InputLabel>
                        <Select
                          value={filterAnnotator}
                          label={t("admin.annotators.filters.filter_annotators")}
                          onChange={(e) => setFilterAnnotator(e.target.value)}
                          sx={{
                            height: "56px",
                          }}
                        >
                          <MenuItem value="Created By Me">
                            <em>{t("admin.annotators.filters.own")}</em>
                          </MenuItem>
                          <MenuItem value="All">
                            <em>{t("admin.annotators.filters.all")}</em>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        onClick={handleFilterReset}
                      >
                        {t("superAdmin.buttons.reset")}
                      </MDButton>
                    </Grid>
                  </Grid>
                </Card>
              </MDBox>
            )}

            {/* Table */}
            <MDBox pt={3} pb={3}>
              <Card sx={{ boxShadow: 4, borderRadius: 2 }}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted
                  entriesPerPage={{
                    defaultValue: 10,
                    entries: [5, 10, 15, 20, 25],
                  }}
                  showTotalEntries
                  canSearch
                  pagination={{ variant: "gradient", color: "info" }}
                />
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Modals */}
      <AddAnnotator
        open={openAddAnnotator}
        onClose={() => setOpenAddAnnotator(false)}
        onSuccess={fetchAnnotatorsData}
      />

      <EditUser
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        user={selectedUser}
        onSuccess={fetchAnnotatorsData}
      />

      <ResetPassword
        open={openReset}
        onClose={() => setOpenReset(false)}
        user={selectedUser}
        onSuccess={fetchAnnotatorsData}
      />

      <ReassignSamples
        open={openReassign}
        onClose={() => setOpenReassign(false)}
        user={selectedUser}
        onSuccess={fetchAnnotatorsData}
      />

      <DeleteUser
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        user={selectedUser}
        onSuccess={fetchAnnotatorsData}
      />
    </DashboardLayout>
  );
}

export default ManageAnnotators;
